import { readLeadSource } from './lead-source.js';

// Every form printed by condor_render_form() posts here; the server validates against the
// form's own config, so this file only handles reCAPTCHA, the request and the result.
const RECAPTCHA_SITE_KEY = '6LcXEcsbAAAAACI79HkuXwvBm-ap6pOItplTXoZ5';
const DEFAULT_ERROR = 'There was an error sending your message. Please try again.';
const MAX_FILE_BYTES = 2 * 1024 * 1024;
const ALLOWED_FILE_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

let recaptchaReady = null;

function loadRecaptcha() {
    if (recaptchaReady) return recaptchaReady;

    recaptchaReady = new Promise((resolve, reject) => {
        if (typeof window.grecaptcha?.execute === 'function') {
            window.grecaptcha.ready(resolve);
            return;
        }
        const script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
        script.async = true;
        script.onload = () => window.grecaptcha.ready(resolve);
        script.onerror = () => {
            recaptchaReady = null;
            reject(new Error('reCAPTCHA failed to load'));
        };
        document.body.appendChild(script);
    });
    return recaptchaReady;
}

async function recaptchaToken() {
    await loadRecaptcha();
    return window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'submit' });
}

// Reuses the registered conversion (with its value) when the label matches one in enqueue.php.
function reportConversion(sendTo) {
    if (!sendTo || typeof window.gtag !== 'function') return;

    const registered = Object.values(window.condorGoogleAdsConversions ?? {}).find((conversion) => conversion.send_to === sendTo);
    window.gtag('event', 'conversion', registered ?? { send_to: sendTo });
}

function checkFile(event) {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > MAX_FILE_BYTES) {
        alert('File size must be less than 2MB. Please choose a smaller file.');
        event.target.value = '';
    } else if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        alert('Please upload a PDF, DOC, or DOCX file.');
        event.target.value = '';
    }
}

function showSuccess(form, formContent, successMessage) {
    formContent.style.opacity = '0';
    formContent.style.transition = 'opacity 0.3s ease';

    setTimeout(() => {
        successMessage.classList.remove('hidden');
        successMessage.style.opacity = '0';
        successMessage.offsetHeight;
        successMessage.style.transition = 'opacity 0.3s ease';
        successMessage.style.opacity = '1';
        form.reset();
    }, 300);
}

function initForm(form) {
    const formContent = form.querySelector('.form-content');
    const successMessage = form.querySelector('.success-message');
    const submitButton = form.querySelector('button[type="submit"]');
    if (!formContent || !successMessage || !submitButton) return;

    const fileInput = form.querySelector('input[type="file"]');
    fileInput?.addEventListener('change', checkFile);

    form.querySelectorAll('input, textarea, select').forEach((input) => {
        input.addEventListener('focus', () => loadRecaptcha().catch(() => {}), { once: true });
    });

    form.addEventListener('submit', async (event) => {
        event.preventDefault();

        if (typeof ajax_object === 'undefined' || !ajax_object.ajax_url) {
            alert('Configuration error. Please try again later.');
            return;
        }

        const originalButtonText = submitButton.textContent;
        submitButton.textContent = fileInput ? 'Uploading...' : 'Sending...';
        submitButton.disabled = true;

        try {
            form.elements.recaptcha_response.value = await recaptchaToken();
            form.elements.lead_source.value = JSON.stringify(readLeadSource() ?? {});

            const formData = new FormData(form);
            formData.append('action', 'condor_form_submit');

            const response = await fetch(ajax_object.ajax_url, {
                method: 'POST',
                credentials: 'same-origin',
                body: fileInput ? formData : new URLSearchParams(formData),
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const result = await response.json();
            if (!result.success) {
                alert(result.data?.message || DEFAULT_ERROR);
                return;
            }

            reportConversion(result.data.conversion_send_to);
            if (result.data.redirect_url) {
                window.location.assign(result.data.redirect_url);
                return;
            }
            showSuccess(form, formContent, successMessage);
        } catch (error) {
            console.error('Form submission error:', error);
            alert(DEFAULT_ERROR);
        } finally {
            submitButton.textContent = originalButtonText;
            submitButton.disabled = false;
        }
    });
}

export default function initForms() {
    document.querySelectorAll('form[data-condor-form-id]').forEach(initForm);
}
